import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';

export type TeamForProjectOrReleaseGroup = {
  id: number;
  name: string;
  organizationId: number;
};

export type TeamProjectResponse = {
  totalCount: number;
  teams: TeamForProjectOrReleaseGroup[];
};

async function fetchTeams(projectLocator?: string, releaseGroupId?: number) {
  if (projectLocator) {
    return get<TeamProjectResponse>(`/api/projects/${nuc(projectLocator)}/teams`);
  }

  return get<TeamProjectResponse>(`/api/project_group/${releaseGroupId}/teams`);
}

function getTeamsKey(projectLocator?: string, releaseGroupId?: number): QueryKey {
  if (projectLocator) {
    return ['projects', projectLocator, 'teams'];
  }

  return ['releaseGroups', releaseGroupId, 'teams'];
}

/**
 * Hook to return the teams for a specified project or release group.
 *
 * @param projectLocator The project locator if it is a project.
 * @param releaseGroupId The release group ID if a release group.
 */
export default function useGetTeamsForProjectOrReleaseGroup(
  projectLocator?: string,
  releaseGroupId?: number,
) {
  return useQuery<TeamProjectResponse>({
    enabled: !!projectLocator || !!releaseGroupId,
    queryKey: getTeamsKey(projectLocator, releaseGroupId),
    queryFn: () => fetchTeams(projectLocator, releaseGroupId),
  });
}
